// TODO: Refactor parseZip and parseMsprFiles
import JSZip from "jszip";
import { v4 as uuidv4 } from 'uuid';
import { ExportTarget } from "../pages/converter/PresetConverter";
import { convertPreset } from "./converter";
import Mspr from "./Mspr";
import { MotorSynthPresets, MS_PRESET_EXTENSION, Preset, PresetContents } from "./types";

const PATTERNS = {
  versionHeader: /^PRESET_FILE_V;.+$/,
  // matches: '[...]/B{bankId}_{bankName}' -> [bankId, bankName] (1.2+ format)
  folder: new RegExp('.*/?B(\\d{1,3})_([^/]+)'),
  // Matches files: '[.../]B{bankId}_{bankName}/P{presetId}_{presetName}.mspr' -> [path, bankId, bankName, presetId, presetName]
  presetPath: new RegExp(`.*/?B(\\d{1,3})_([^/]+)/P(\\d{1,3})_([^/]+)\.mspr$`),
  // Matches: '[.../]{BankName}' -> [prefix,bankName] (v1 format)
  legacyFolder: new RegExp('(.*/)?([^/]+)'),
  // Matches files: '[.../]{BankName}/{PresetName}.mspr' -> [path, prefix, bankName, presetName]
  legacyPresetPath: new RegExp(`(.*/)?([^/]+)/([^/]+)\.mspr$`),
  // Matches: '(P{presetIndex}_)?{PresetName}.mspr -> [presetName]
  presetName: new RegExp(`^(P\\d{1,3}_)?([^/]+)\.mspr$`),
}

const VERSION_HEADER = 'PRESET_FILE_V';

interface RawPreset {
  path: string;
  // lazy loading, since we sometimes filter presets based on the name
  contents: () => Promise<string>;
}

export const parseZip = async (
  fs: File,
  target: ExportTarget,
  presetContents: PresetContents,
  tree: MotorSynthPresets,
): Promise<[MotorSynthPresets, PresetContents, string[]]> => {
  const zip = await JSZip.loadAsync(fs);
  const rawPresets = zip.file(/.*/)
    .filter(f => !f.name.includes('__MACOSX'))
    .filter(f => !f.name.includes('.DS_Store'))
    .map(f => ({
      path: f.name,
      contents: () => f.async('string'),
    }) as RawPreset)
    .filter(f => f.path.endsWith(MS_PRESET_EXTENSION));

  return parseRawPresets(rawPresets, target, presetContents, tree);
}

export const parseMsprFiles = async (
  files: File[],
  target: ExportTarget,
  presetContents: PresetContents,
  tree: MotorSynthPresets,
): Promise<[MotorSynthPresets, PresetContents, string[]]> => {
  const presets = files
    .filter(f => f.name.endsWith(MS_PRESET_EXTENSION))
    .map((f) => {
      return {
        path: f.webkitRelativePath || f.name,
        contents: () => {
          try {
            return f.text();
          } catch (e: any) {
            // what we get if we parse a directory
            // FIXME: Clean this up
            if ((e as Error).message.includes('Blob')) {
              const reader = new FileReader();
              reader.readAsText(f);
              return new Promise<string>((resolve, reject) => {
                reader.onload = () => {
                  if (typeof reader.result === 'string') {
                    resolve(reader.result);
                  } else {
                    reject(new Error('Failed to read preset file'));
                  }
                };
                reader.onerror = () => {
                  reject(new Error('Failed to read preset file'));
                };
              }
              );
            }
            throw e;
          }
        },
      }
    });
  return parseRawPresets(presets, target, presetContents, tree);
}

const parseRawPresets = async (
  rawPresets: RawPreset[],
  target: ExportTarget,
  presetContents: PresetContents,
  tree: MotorSynthPresets,
): Promise<[MotorSynthPresets, PresetContents, string[]]> => {
  const skippedPresets: string[] = [];

  ///////////////////////////
  // BANKS
  ///////////////////////////
  const folderNames = rawPresets
    // to File
    .filter(f => f.path.endsWith(MS_PRESET_EXTENSION))
    // SOME/DIR/BANK/PRESET.mspr -> SOME/DIR/BANK/
    .map(f => f.path.substring(0, f.path.lastIndexOf('/')))
    // dedupe
    .filter((v, i, a) => a.indexOf(v) === i);

  // Same ID, different name
  const conflictedBanks = new Set<[number, string]>();
  // First pass -> add all BXXX_ banks
  for (const folder of folderNames) {
    const [, bankId, bankName] = PATTERNS.folder.exec(folder) || [];
    if (!bankId) continue;  // legacy/non-MS bank name
    const parsedId = parseInt(bankId!, 10);
    if (bankId && parsedId >= 0 && parsedId < Mspr.MAX_SLOTS) {
      // same ID as existing - add later
      if (tree.banks[parsedId].name && tree.banks[parsedId].name !== bankName) {
        conflictedBanks.add([parsedId, bankName]);
      }
      tree.banks[parsedId].name = bankName;
    }
  }

  // first empty bank, reversed so we can pop like a queue
  // add conflicts (same BXXX ID, different name)
  for (const [, bankName] of conflictedBanks) {
    // first empty bank
    const newBankId = tree.banks.findIndex(b => b && !b.name);
    if (newBankId > -1) {
      tree.banks[newBankId].name = bankName;
    }
    // if no empty banks, skip TODO: report error?
  }

  // add other folders (non-BXXX_)
  for (const folderName of folderNames) {
    if (PATTERNS.folder.test(folderName)) continue;  // skip BXXX_ folders
    const [, , bankName] = PATTERNS.legacyFolder.exec(folderName) || [];
    if (!bankName) continue;
    // first empty bank
    const newBankId = tree.banks.findIndex(b => !b.name);
    if (newBankId > -1) {   // if no empty banks, skip TODO: report error?
      tree.banks[newBankId].name = bankName;
    }
  }

  ////////////////////////////
  // PRESTS
  ////////////////////////////

  const conflictedPresets = [];   // TODO: Handle conflicts
  // add all PXXX presets
  const newPresets = rawPresets.filter(f => PATTERNS.presetPath.test(f.path));
  for (const file of newPresets) {
    const [, bankId, , presetId, presetName] = PATTERNS.presetPath.exec(file.path)!;

    let [succeeded, payload] = processPreset(await file.contents(), file.path, target);
    if (!succeeded) {
      skippedPresets.push(file.path);
      continue;
    }

    // P1 - P128 -> 0 - 127 index
    const presetIdInt = parseInt(presetId, 10) - 1;
    const bankIdInt = parseInt(bankId, 10);
    const presetUuid = uuidv4();
    tree.banks[bankIdInt].presets[presetIdInt] = {
      name: presetName,
      uniqueId: presetUuid,
    };
    presetContents[presetUuid] = payload;
  }

  // add all legacy presets to first available slot (ignoring folder structure)
  const legacyPresets = rawPresets.filter(f => PATTERNS.legacyPresetPath.test(f.path) && !PATTERNS.presetPath.test(f.path));
  for (const file of legacyPresets) {
    const [, , bankName, presetName] = PATTERNS.legacyPresetPath.exec(file.path)!;

    // Payload conversion TODO: Maybe move elsewhere?
    let [succeeded, payload] = processPreset(await file.contents(), file.path, target);
    if (!succeeded) {
      skippedPresets.push(file.path);
      continue;
    }
    const presetUuid = uuidv4();
    // HACK: This will put presets in the wrong folder, if some folders are banks are named the same
    const newBankId = tree.banks.findIndex(b => (b.name === bankName && !Mspr.isFull(b)));
    if (newBankId > -1) {   // if no empty banks, skip TODO: report error?
      Mspr.addPreset(tree.banks[newBankId], {
        name: presetName,
        uniqueId: presetUuid,
      });
      presetContents[presetUuid] = payload;
    } else {
      // TODO: Report error
      console.warn('No empty banks to add preset to: ', presetName);
    }
  }

  // handle leaf-node files
  const leafFiles = rawPresets.filter(f => !f.path.includes('/') && PATTERNS.presetName.test(f.path));
  for (const file of leafFiles) {
    const [,,presetName] = PATTERNS.presetName.exec(file.path)!;
    let [succeeded, payload] = processPreset(await file.contents(), file.path, target);
    if (!succeeded) {
      skippedPresets.push(file.path);
      continue;
    }
    const presetUuid = uuidv4();
    // add in first empty slot
    const newBankId = tree.banks.findIndex(b => !Mspr.isFull(b));
    if (newBankId > -1) {   // if no empty banks, skip TODO: report error?
      Mspr.addPreset(tree.banks[newBankId], {
        name: presetName,
        uniqueId: presetUuid,
      });
      presetContents[presetUuid] = payload;
    } else {
      // TODO: add to "skippedBecauseFullPresets"
      console.warn('No empty banks to add preset to: ', presetName);
    }
  }

  return [tree, presetContents, skippedPresets];
}

const shouldSkipPreset = (payload: string, path: string, target: ExportTarget) => {
  if (!isValidPreset(payload)) {
    console.warn(`Invalid preset: ${path}`);
    return true;
  };
  if (payload.startsWith(VERSION_HEADER)) {
    const [, version] = payload.split('\n')[0].split(';');
    const parsedVersion = Number.parseFloat(version);
    if (Number.isNaN(parsedVersion)) return true;
    if (target === 'MK2' && parsedVersion < 2.0) return true;
    if (target === 'MKv1.2' && parsedVersion >= 2.0) return true;
  } else if (!payload.startsWith(VERSION_HEADER) && target === 'MK2') {
    return true;
  }
  return false;
}

const processPreset = (payload: string, path: string, target: ExportTarget): [boolean, string] => {
  let payloadCopy = payload.repeat(1);
  if (shouldSkipPreset(payload, path, target)) {
    return [false, ''];
  }
  if (!payloadCopy.startsWith(VERSION_HEADER)) {
    // adds the 1.2 header
    payloadCopy = convertPreset(payloadCopy);
  }
  return [true, payloadCopy];
}

export const convertAndDownloadZip = ({ banks }: MotorSynthPresets, presetContents: PresetContents, zipName: string) => {
  // Create directory structure of:
  // zipName
  // - PRESETS
  //   - B{bankId}_{bankName}
  //     - P{presetId}_{presetName}.mspr
  const zip = new JSZip();
  const presetsFolder = zip.folder('PRESETS')!;
  let bankFolder;
  // filter out empty banks
  const nonEmptyBanks = banks.filter(b => b.name || !Mspr.isEmpty(b));
  // TODO: Sanitize names (non ascii, filename friendly etc. Right now only done on the frontend)
  for (let i = 0; i < nonEmptyBanks.length; i++) {
    const bank = nonEmptyBanks[i];
    bank.name = bank.name || Mspr.DEFAULT_BANK_NAME(i);
    const bankFolderName = `B${i}_${bank.name}`;
    bankFolder = presetsFolder.folder(bankFolderName)!;
    const definedPresets = bank.presets.filter(p => p !== undefined) as Preset[];
    
    for (let j = 0; j < definedPresets.length; j++) {
      const preset = definedPresets[j];
      // B0-B127, but P1-P128
      const presetFileName = `P${j + 1}_${preset.name}${MS_PRESET_EXTENSION}`;
      // HACK: No idea why double carriage returns appear
      const presetPayload = presetContents[preset.uniqueId].replaceAll('\r\r', '\r');
      bankFolder.file(presetFileName, presetPayload);
    }
  }

  // Download Zip
  zip.generateAsync({ type: 'blob' }).then((content) => {
    const url = window.URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipName;
    a.click();
  });
}

/**
 * Ensures the file is a valid MotorSynth preset file.
 * 
 * Expected format:
 * key;param,values
 * 
 * Examples:
 * P_VCF_V2_ATTACK;0.00500
 * ARP_RYTHM_STEP;14,1.00000,0.00000,1.00000,1.00000,0
 */
export const isValidPreset = (presetLines: string) => {
  const lines = presetLines.trim().split(/\r?\n/);
  const validLines = lines.filter((line) => {
    const [key, value] = line.split(';');
    return key && value;
  });
  return validLines.length === lines.length;
}

export const parseDirectoryFiles = async (item: FileSystemEntry): Promise<FileSystemFileEntry[]> => {
  if (!item || !item.isDirectory) return Promise.resolve([]);
  const directoryReader: FileSystemDirectoryReader = (item as any)!.createReader();
  const entries: FileSystemEntry[] = [];
  // scan all entries in directory
  const readEntries = (resolve: (value: any) => void, reject: (reason: Error) => void) => {
    directoryReader.readEntries((results: any) => {
      if (!results.length) {
        resolve(entries);
      }
      entries.push(...results);
      readEntries(resolve, reject);
    }, reject);
  };

  return new Promise((resolve: (e: FileSystemEntry[]) => void, reject) => readEntries(resolve, reject))
    .then(async (entries) => {
      // recurse over directory
      const promises = entries.map((entry: FileSystemEntry) => {
        if (entry.isDirectory) {
          return parseDirectoryFiles(entry);
        }
        return Promise.resolve([entry]);
      });
      const files = await Promise.all(promises);
      return files.reduce((acc, val) => acc.concat(val), [] as any[]);
    });
}
